import { useEffect, useReducer } from 'react';
import { Sparkles } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormField } from '@/components/ui/form-field';
import { PasswordInput } from '@/components/ui/password-input';
import { useAuthModal } from '@/stores/auth-modal';
import { authClient } from '@/lib/auth-client';
import { useAuth } from '@/stores/auth';
import { toast } from 'sonner';

type ModalState = {
  tab: 'signup' | 'signin';
  isSubmitting: boolean;
  error: string | null;
  notice: string | null;
};

type ModalAction =
  | { type: 'RESET'; mode: 'signup' | 'signin' }
  | { type: 'SWITCH_TAB'; tab: 'signup' | 'signin' }
  | { type: 'SIGNUP_SUCCESS' }
  | { type: 'START_SUBMIT' }
  | { type: 'END_SUBMIT' }
  | { type: 'SET_ERROR'; error: string | null }
  | { type: 'SET_NOTICE'; notice: string | null };

const modalReducer = (state: ModalState, action: ModalAction): ModalState => {
  switch (action.type) {
    case 'RESET':
      return { tab: action.mode, isSubmitting: false, error: null, notice: null };
    case 'SWITCH_TAB':
      return { ...state, tab: action.tab, error: null, notice: null };
    case 'SIGNUP_SUCCESS':
      return { ...state, tab: 'signin', isSubmitting: false, notice: 'Check your email to verify your account before signing in.' };
    case 'START_SUBMIT':
      return { ...state, isSubmitting: true, error: null, notice: null };
    case 'END_SUBMIT':
      return { ...state, isSubmitting: false };
    case 'SET_ERROR':
      return { ...state, error: action.error };
    case 'SET_NOTICE':
      return { ...state, notice: action.notice };
    default:
      return state;
  }
};

export const AuthModal = () => {
  const { open, mode, prompt, closeAuth } = useAuthModal();
  const { refresh } = useAuth();
  const [state, dispatch] = useReducer(modalReducer, { tab: mode, isSubmitting: false, error: null, notice: null });
  const { tab, isSubmitting, error, notice } = state;
  const isSignUp = tab === 'signup';

  // Single dispatch instead of 4 sequential setState calls — avoids cascading re-renders.
  useEffect(() => {
    if (open) dispatch({ type: 'RESET', mode });
  }, [open, mode]);

  const switchTab = (next: 'signup' | 'signin') => {
    dispatch({ type: 'SWITCH_TAB', tab: next });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    dispatch({ type: 'START_SUBMIT' });
    const formData = new FormData(e.currentTarget);
    const email = String(formData.get('email') ?? '');
    const password = String(formData.get('password') ?? '');
    const name = String(formData.get('name') ?? '');

    try {
      const result = isSignUp
        ? await authClient.signUp.email({
            email,
            password,
            name: name || email.split('@')[0]!,
            callbackURL: '/signin?verified=1',
          })
        : await authClient.signIn.email({ email, password });

      if (result.error) {
        dispatch({ type: 'SET_ERROR', error: result.error.message ?? 'Authentication failed.' });
        return;
      }
      if (isSignUp) {
        dispatch({ type: 'SIGNUP_SUCCESS' });
        toast.success('Verification email sent', {
          description: 'Use the link in your inbox to activate your account.',
        });
        return;
      }
      await refresh();
      closeAuth();
      toast.success('Welcome back', {
        description: 'Your saved prompts and collections are waiting.',
      });
    } catch (err) {
      dispatch({ type: 'SET_ERROR', error: err instanceof Error ? err.message : 'Authentication failed.' });
    } finally {
      dispatch({ type: 'END_SUBMIT' });
    }
  };

  const handleGoogle = async () => {
    dispatch({ type: 'START_SUBMIT' });
    try {
      await authClient.signIn.social({ provider: 'google', callbackURL: '/' });
    } catch (err) {
      dispatch({ type: 'SET_ERROR', error: err instanceof Error ? err.message : 'Google sign-in failed.' });
    } finally {
      dispatch({ type: 'END_SUBMIT' });
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) closeAuth();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2 text-accent text-xs font-medium tracking-wide uppercase">
            <Sparkles className="h-3.5 w-3.5" />
            {isSignUp ? 'Free forever' : 'Welcome back'}
          </div>
          <DialogTitle className="text-2xl">
            {isSignUp
              ? 'Create a free account to reveal prompts'
              : 'Sign in to your Visualry account'}
          </DialogTitle>
          <DialogDescription>
            {isSignUp
              ? 'Browse AI images, reveal the prompts behind them, save collections, and generate similar visuals when you need them.'
              : 'Pick up where you left off — your saved prompts and collections are waiting.'}
          </DialogDescription>
        </DialogHeader>

        {notice && (
          <output
            className="block rounded-md border border-primary/30 bg-primary/10 px-3 py-2 text-xs text-primary"
          >
            {notice}
          </output>
        )}

        {prompt && (
          <div className="rounded-md border border-border-subtle bg-surface-soft px-3 py-2 text-xs text-text-secondary">
            {prompt}
          </div>
        )}

        {error && (
          <div
            role="alert"
            aria-live="polite"
            className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-xs text-danger"
          >
            {error}
          </div>
        )}

        <div className="grid gap-3">
          <Button
            variant="secondary"
            className="w-full"
            onClick={handleGoogle}
            disabled={isSubmitting}
          >
            Continue with Google
          </Button>
          <div className="flex items-center gap-3 text-xs text-text-muted">
            <span className="h-px flex-1 bg-border-subtle" />
            or
            <span className="h-px flex-1 bg-border-subtle" />
          </div>
          <form className="grid gap-3" onSubmit={handleSubmit}>
            {isSignUp && (
              <FormField id="auth-name" label="Display name">
                {(props) => (
                  <Input
                    {...props}
                    name="name"
                    type="text"
                    placeholder="Display name (optional)"
                    autoComplete="name"
                  />
                )}
              </FormField>
            )}
            <FormField id="auth-email" label="Email" required>
              {(props) => (
                <Input
                  {...props}
                  name="email"
                  type="email"
                  required
                  placeholder="you@studio.com"
                  autoComplete="email"
                />
              )}
            </FormField>
            <FormField id="auth-password" label="Password" required hint={isSignUp ? '8+ characters' : undefined}>
              {(props) => (
                <PasswordInput
                  {...props}
                  name="password"
                  required
                  minLength={8}
                  autoComplete={isSignUp ? 'new-password' : 'current-password'}
                  placeholder="Password (8+ chars)"
                />
              )}
            </FormField>
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting
                ? isSignUp
                  ? 'Creating account…'
                  : 'Signing in…'
                : isSignUp
                  ? 'Create free account'
                  : 'Sign in'}
            </Button>
          </form>
        </div>

        <p className="text-center text-xs text-text-muted">
          {isSignUp ? (
            <>
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => switchTab('signin')}
                className="text-foreground underline-offset-4 hover:underline"
              >
                Sign in
              </button>
            </>
          ) : (
            <span className="inline-flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
              New to Visualry?{' '}
              <button
                type="button"
                onClick={() => switchTab('signup')}
                className="text-foreground underline-offset-4 hover:underline"
              >
                Create a free account
              </button>
              <span aria-hidden="true">·</span>
              <button
                type="button"
                onClick={() => {
                  closeAuth();
                  window.location.assign('/forgot-password');
                }}
                className="text-foreground underline-offset-4 hover:underline"
              >
                Forgot password?
              </button>
            </span>
          )}
        </p>
      </DialogContent>
    </Dialog>
  );
};
